import { ICellEditorComp, ICellEditorParams } from '@ag-grid-community/core';

/* eslint @typescript-eslint/explicit-module-boundary-types: 0 */
/* eslint class-methods-use-this: 0 */
// backspace starts the editor on Windows
const KEY_BACKSPACE = 'Backspace';
const ALLOWED_CTRL_KEYS = ['v', 'c', 'a', 'z'];

export class NumericCellEditor implements ICellEditorComp {
  eInput!: HTMLInputElement;

  cancelBeforeStart!: boolean;

  // gets called once before the renderer is used
  init(params: ICellEditorParams) {
    // create the cell
    this.eInput = document.createElement('input');
    this.eInput.classList.add('ag-input-field-input', 'ag-text-field-input');

    this.eInput.value = params.data?.modifiedValue ? params.data.modifiedValue.toString() : '';

    // Access the event directly within the keydown event listener
    this.eInput.addEventListener('keydown', (event) => {
      const eventKey = event.key;
      if (!eventKey || eventKey.length !== 1) {
        return;
      }
      if (
        !this.isNumericKey(eventKey) &&
        !this.isBackspace(eventKey) &&
        !this.isMinusSign(eventKey) &&
        !this.isCtrl(event)
      ) {
        this.eInput.focus();
        if (event.preventDefault) {
          event.preventDefault();
        }
      } else if (this.isNavigationKey(eventKey) || this.isBackspace(eventKey)) {
        event.stopPropagation();
      }
    });

    this.eInput.addEventListener('paste', (event) => {
      const { clipboardData } = event;
      const pastedText = clipboardData?.getData('text');

      if (!this.isPastedTextValid(pastedText)) {
        this.eInput.focus();
        if (event.preventDefault) {
          event.preventDefault();
        }
      }
    });

    // only start edit if key pressed is a number, a decimal point, or a minus sign
    const isNotANumber =
      params.eventKey && params.eventKey.length === 1 && '1234567890.-.'.indexOf(params.eventKey) < 0;
    this.cancelBeforeStart = !!isNotANumber;
  }

  // Logic to determine if pasted text is valid
  isPastedTextValid(text: string | undefined) {
    const sanitizedText = text?.replace(',', '');
    return sanitizedText === undefined || /^-?\d*\.?\d*$/.test(sanitizedText);
  }

  isBackspace(eventKey: string) {
    return eventKey === KEY_BACKSPACE;
  }

  isNavigationKey(eventKey: string) {
    return eventKey === 'ArrowLeft' || eventKey === 'ArrowRight';
  }

  isMinusSign(eventKey: string) {
    return eventKey === '-';
  }

  isCtrl(event: any) {
    return ALLOWED_CTRL_KEYS.includes(event.key) && (event.ctrlKey || event.metaKey);
  }

  // gets called once when grid ready to insert the element
  getGui() {
    return this.eInput;
  }

  // focus and select can be done after the gui is attached
  afterGuiAttached() {
    this.eInput.focus();
  }

  // returns the new value after editing
  isCancelBeforeStart() {
    return this.cancelBeforeStart;
  }

  // example - will reject the number if it greater than 1,000,000
  // - not very practical, but demonstrates the method.
  isCancelAfterEnd() {
    const value = this.getValue();
    return value != null && value > 1000000;
  }

  // returns the new value after editing
  getValue() {
    const { value } = this.eInput;
    return value === '' || value == null ? null : parseFloat(value.replace(',', ''));
  }

  // if true, then this editor will appear in a popup
  isPopup() {
    // and we could leave this method out also, false is the default
    return false;
  }

  isCharNumeric(charStr: string | null) {
    return charStr && !!/\d/.test(charStr);
  }

  isNumericKey(eventKey: string) {
    return this.isCharNumeric(eventKey) || eventKey === '.' || (eventKey === '-' && this.eInput.selectionStart === 0);
  }
}
